import { useState, useRef, useEffect } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { toast } from "sonner";
import { money, formatDateAT } from "@/lib/format";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Upload, FileSpreadsheet, CheckCircle2, AlertTriangle, X, Sparkles, Repeat, Undo2 } from "lucide-react";

const T = {
  tr: {
    title: "Banka Ekstresi İçe Aktar",
    desc: "BKS Umsatzübersicht / Kontoauszug yükle. Öğrenilmiş banka DNA'sı ile otomatik sınıflandırma yapılır.",
    upload: "CSV dosyası yükle",
    or: "veya",
    pasteLabel: "CSV / satır satır işlem verisini yapıştır",
    pastePh: "Buchungsdatum;Betrag;Text\n2026-07-05;-1092,74;MDJ Handels GmbH Teilzahlung...",
    analyze: "Ön izleme oluştur",
    kindRevenue: "Gelir",
    kindExpense: "Gider",
    kindSkip: "Atla",
    high: "Yüksek",
    medium: "Orta",
    low: "Düşük",
    dup: "Yinelenen",
    totalRows: "Toplam işlem",
    importable: "İçe aktarılabilir",
    duplicates: "Yinelenen",
    inflow: "Toplam giriş",
    outflow: "Toplam çıkış",
    commit: "Seçili işlemleri içe aktar",
    bankAcc: "Banka hesabı",
    matchedAs: "Eşleşme",
    notMatched: "Eşleşme yok",
    resultCreatedRev: "Gelir eklendi",
    resultCreatedExp: "Gider eklendi",
    resultSkippedDup: "Yinelenen atlandı",
    resultSkippedManual: "Manuel atlandı",
    conf: "Güven",
    reset: "Sıfırla",
    empty: "Henüz ön izleme yok. CSV yükle veya yapıştır ve 'Ön izleme oluştur' tıkla.",
    dropHere: "CSV'yi buraya sürükle",
    rollbackBtn: "Son içe aktarmayı geri al",
    rollbackConfirmTitle: "Son içe aktarma geri alınsın mı?",
    rollbackConfirmDesc: "Bu partideki tüm gelir ve gider satırları görünmez olarak işaretlenir. Silinmez. OCR faturaları, bordro, DNA ve manuel kayıtlar etkilenmez.",
    rollbackCancel: "Vazgeç",
    rollbackConfirm: "Evet, geri al",
    rollbackDoneTitle: "Geri alma tamamlandı",
    rollbackBatch: "Parti kimliği",
    rollbackRev: "Gizlenen gelir satırı",
    rollbackExp: "Gizlenen gider satırı",
    rollbackTotal: "Toplam çevrilen tutar",
    rollbackTs: "Zaman damgası",
    noBatchFound: "Geri alınacak parti bulunamadı",
  },
  de: {
    title: "Bankauszug importieren",
    desc: "BKS Umsatzübersicht / Kontoauszug hochladen. Automatische Klassifizierung über gelernte Bank-DNA.",
    upload: "CSV-Datei hochladen",
    or: "oder",
    pasteLabel: "CSV / zeilenweise Transaktionsdaten einfügen",
    pastePh: "Buchungsdatum;Betrag;Text\n2026-07-05;-1092,74;MDJ Handels GmbH Teilzahlung...",
    analyze: "Vorschau erstellen",
    kindRevenue: "Einnahme",
    kindExpense: "Ausgabe",
    kindSkip: "Überspringen",
    high: "Hoch",
    medium: "Mittel",
    low: "Niedrig",
    dup: "Duplikat",
    totalRows: "Transaktionen",
    importable: "Importierbar",
    duplicates: "Duplikate",
    inflow: "Zufluss gesamt",
    outflow: "Abfluss gesamt",
    commit: "Ausgewählte importieren",
    bankAcc: "Bankkonto",
    matchedAs: "Erkannt als",
    notMatched: "Nicht erkannt",
    resultCreatedRev: "Einnahmen erstellt",
    resultCreatedExp: "Ausgaben erstellt",
    resultSkippedDup: "Duplikate übersprungen",
    resultSkippedManual: "Manuell übersprungen",
    conf: "Konfidenz",
    reset: "Zurücksetzen",
    empty: "Noch keine Vorschau. CSV hochladen oder einfügen und 'Vorschau erstellen' klicken.",
    dropHere: "CSV hier ablegen",
    rollbackBtn: "Letzten Import rückgängig machen",
    rollbackConfirmTitle: "Letzten Bankimport zurücksetzen?",
    rollbackConfirmDesc: "Alle Einnahme- und Ausgabezeilen dieses Batches werden als versteckt markiert. Nichts wird gelöscht. OCR-Rechnungen, Lohn-Seeds, DNA und manuelle Einträge bleiben unberührt.",
    rollbackCancel: "Abbrechen",
    rollbackConfirm: "Ja, zurücksetzen",
    rollbackDoneTitle: "Rückgängig gemacht",
    rollbackBatch: "Batch-ID",
    rollbackRev: "Ausgeblendete Einnahmen",
    rollbackExp: "Ausgeblendete Ausgaben",
    rollbackTotal: "Reversierte Gesamtsumme",
    rollbackTs: "Zeitstempel",
    noBatchFound: "Kein zurücksetzbarer Batch gefunden",
  },
};

const CONF_CLASS = (c) => c >= 0.9 ? "bg-emerald-100 text-emerald-800 border-emerald-200"
                       : c >= 0.6 ? "bg-amber-100 text-amber-800 border-amber-200"
                                  : "bg-rose-100 text-rose-800 border-rose-200";

export default function BankImportTab() {
  const { lang } = useI18n();
  const t = T[lang] || T.tr;
  const [csvText, setCsvText] = useState("");
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [drag, setDrag] = useState(false);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [bankAcc, setBankAcc] = useState("");
  const [result, setResult] = useState(null);
  const [lastBatch, setLastBatch] = useState(null);         // {batch_id, created_at, revenue_rows, expense_rows}
  const [rollbackOpen, setRollbackOpen] = useState(false);
  const [rollbackResult, setRollbackResult] = useState(null);
  const fileRef = useRef(null);

  // Load bank accounts + last active batch on mount
  useEffect(() => {
    api.get("/bank-accounts").then((r) => {
      setBankAccounts(r.data || []);
      if (r.data?.[0]?.id) setBankAcc(r.data[0].id);
    }).catch(() => {});
    api.get("/bank-import/last-batch").then((r) => {
      setLastBatch(r.data && r.data.batch_id ? r.data : null);
    }).catch(() => {});
  }, []);

  const doPreview = async () => {
    if (!csvText.trim()) {
      toast.error(lang === "tr" ? "CSV metni gerekli" : "CSV-Text erforderlich");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/bank-import/preview", { csv_text: csvText });
      // default kind = classifier suggestion; duplicates + low-conf default to skip
      data.rows = (data.rows || []).map((r) => ({
        ...r,
        selected_kind: r.is_duplicate ? "skip" : (r.confidence < 0.4 ? "skip" : r.kind),
      }));
      setPreview(data);
      setResult(null);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const doFile = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/bank-import/preview-file", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      data.rows = (data.rows || []).map((r) => ({
        ...r,
        selected_kind: r.is_duplicate ? "skip" : (r.confidence < 0.4 ? "skip" : r.kind),
      }));
      setPreview(data);
      setResult(null);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const setRowKind = (tempId, kind) => {
    setPreview((p) => ({ ...p, rows: p.rows.map((r) => r.temp_id === tempId ? { ...r, selected_kind: kind } : r) }));
  };

  const doCommit = async () => {
    if (!preview) return;
    const decisions = preview.rows.map((r) => ({ ...r, kind: r.selected_kind }));
    const toImport = decisions.filter((d) => d.kind !== "skip").length;
    if (toImport === 0) {
      toast.error(lang === "tr" ? "İçe aktaracak satır yok" : "Nichts zu importieren");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/bank-import/commit", { decisions, bank_account_id: bankAcc || null });
      setResult(data);
      // refresh last-batch pointer so the Rollback button jumps to this new batch
      if (data?.batch_id) {
        setLastBatch({ batch_id: data.batch_id, created_at: new Date().toISOString(),
                       revenue_rows: data.created_revenue, expense_rows: data.created_expenses });
      }
      toast.success(`+${data.created_revenue} · +${data.created_expenses}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const doRollback = async () => {
    if (!lastBatch?.batch_id) {
      toast.error(t.noBatchFound);
      setRollbackOpen(false);
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/bank-import/rollback", { batch_id: lastBatch.batch_id });
      setRollbackResult(data);
      setLastBatch(null);
      toast.success(`↩ ${data.revenue_rows_hidden + data.expense_rows_hidden}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    } finally {
      setBusy(false);
      setRollbackOpen(false);
    }
  };

  const reset = () => { setPreview(null); setResult(null); setCsvText(""); };

  return (
    <div className="space-y-4" data-testid="bank-import-tab">
      {/* Persistent Rollback affordance — visible whenever there is a rollback-able batch */}
      {lastBatch?.batch_id && !rollbackResult && (
        <div className="flex items-center justify-between border rounded-xl px-4 py-3 bg-amber-50 border-amber-200"
             data-testid="bank-import-rollback-bar">
          <div className="text-sm">
            <div className="font-medium text-amber-900">
              {t.rollbackBtn}
            </div>
            <div className="text-xs text-amber-800 font-mono mt-0.5">
              {lastBatch.batch_id} · {lastBatch.revenue_rows} + {lastBatch.expense_rows}
            </div>
          </div>
          <Button variant="outline" size="sm"
                  onClick={() => setRollbackOpen(true)}
                  disabled={busy}
                  data-testid="bank-import-rollback-btn"
                  className="border-amber-300 text-amber-900 hover:bg-amber-100">
            <Undo2 className="w-4 h-4 mr-2" />{t.rollbackBtn}
          </Button>
        </div>
      )}

      {/* Rollback result card */}
      {rollbackResult && (
        <Card data-testid="bank-import-rollback-result">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-amber-800">
              <Undo2 className="w-5 h-5" />{t.rollbackDoneTitle}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <SummaryStat label={t.rollbackRev} value={rollbackResult.revenue_rows_hidden} accent="amber" />
              <SummaryStat label={t.rollbackExp} value={rollbackResult.expense_rows_hidden} accent="amber" />
              <SummaryStat label={t.rollbackTotal} value={money(rollbackResult.total_amount_reversed)} accent="amber" />
              <SummaryStat label={t.rollbackTs} value={formatDateAT(rollbackResult.timestamp)} />
            </div>
            <div className="text-xs text-muted-foreground">
              <span className="uppercase tracking-wide">{t.rollbackBatch}</span>
              <span className="ml-2 font-mono">{rollbackResult.batch_id}</span>
            </div>
            <Button variant="outline" size="sm" onClick={() => setRollbackResult(null)} data-testid="bank-import-rollback-dismiss-btn">
              <X className="w-4 h-4 mr-2" />{t.reset}
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Upload / paste area */}
      {!preview && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2"><Sparkles className="w-4 h-4 text-primary" />{t.title}</CardTitle>
            <p className="text-sm text-muted-foreground">{t.desc}</p>
          </CardHeader>
          <CardContent className="space-y-4">
            <div
              onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
              onDragLeave={() => setDrag(false)}
              onDrop={(e) => { e.preventDefault(); setDrag(false); doFile(e.dataTransfer.files?.[0]); }}
              onClick={() => fileRef.current?.click()}
              className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition ${drag ? "border-primary bg-primary/5" : "border-muted"}`}
              data-testid="bank-import-drop"
            >
              <Upload className="w-8 h-8 mx-auto text-muted-foreground mb-2" />
              <div className="text-sm font-medium">{t.upload}</div>
              <div className="text-xs text-muted-foreground mt-1">{t.dropHere}</div>
              <input ref={fileRef} type="file" accept=".csv,.txt" className="hidden"
                     onChange={(e) => doFile(e.target.files?.[0])} data-testid="bank-import-file-input" />
            </div>
            <div className="text-center text-xs text-muted-foreground">{t.or}</div>
            <div className="space-y-2">
              <label className="text-sm font-medium">{t.pasteLabel}</label>
              <Textarea rows={7} value={csvText} onChange={(e) => setCsvText(e.target.value)}
                        placeholder={t.pastePh} className="font-mono text-xs"
                        data-testid="bank-import-paste-textarea" />
            </div>
            <Button onClick={doPreview} disabled={busy || !csvText.trim()} data-testid="bank-import-analyze-btn">
              <FileSpreadsheet className="w-4 h-4 mr-2" />{t.analyze}
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Preview table */}
      {preview && !result && (
        <>
          {/* Summary */}
          <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
            <SummaryStat label={t.totalRows} value={preview.summary.total} />
            <SummaryStat label={t.importable} value={preview.summary.importable} accent="emerald" />
            <SummaryStat label={t.duplicates} value={preview.summary.duplicates} accent={preview.summary.duplicates ? "amber" : undefined} />
            <SummaryStat label={t.high} value={preview.summary.high_confidence} accent="emerald" />
            <SummaryStat label={t.inflow} value={money(preview.summary.inflow_total)} accent="emerald" />
            <SummaryStat label={t.outflow} value={money(preview.summary.outflow_total)} accent="rose" />
          </div>

          {/* Action bar */}
          <Card>
            <CardContent className="p-4 flex flex-wrap items-center gap-3">
              <div className="flex-1 min-w-[220px]">
                <label className="text-xs text-muted-foreground block mb-1">{t.bankAcc}</label>
                <Select value={bankAcc} onValueChange={setBankAcc}>
                  <SelectTrigger data-testid="bank-import-account-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {bankAccounts.map((a) => (
                      <SelectItem key={a.id} value={a.id}>{a.name} — {money(a.balance)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button onClick={doCommit} disabled={busy} data-testid="bank-import-commit-btn">
                <CheckCircle2 className="w-4 h-4 mr-2" />{t.commit}
              </Button>
              <Button variant="outline" onClick={reset} data-testid="bank-import-reset-btn">
                <X className="w-4 h-4 mr-2" />{t.reset}
              </Button>
            </CardContent>
          </Card>

          {/* Rows */}
          <Card>
            <CardContent className="p-0 overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-b bg-muted/40 text-xs uppercase text-muted-foreground">
                  <tr>
                    <th className="text-left px-3 py-2">{lang === "tr" ? "Tarih" : "Datum"}</th>
                    <th className="text-right px-3 py-2">{lang === "tr" ? "Tutar" : "Betrag"}</th>
                    <th className="text-left px-3 py-2">{t.matchedAs}</th>
                    <th className="text-center px-3 py-2">{t.conf}</th>
                    <th className="text-left px-3 py-2">{lang === "tr" ? "Açıklama" : "Text"}</th>
                    <th className="text-center px-3 py-2">{lang === "tr" ? "Yön" : "Aktion"}</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.rows.map((r) => (
                    <tr key={r.temp_id} className={`border-b last:border-0 ${r.is_duplicate ? "opacity-50 bg-muted/20" : ""}`}
                        data-testid={`bank-import-row-${r.temp_id}`}>
                      <td className="px-3 py-2 whitespace-nowrap">{formatDateAT(r.date)}</td>
                      <td className={`px-3 py-2 text-right font-mono whitespace-nowrap ${r.amount < 0 ? "text-rose-600" : "text-emerald-700"}`}>
                        {money(r.amount)}
                      </td>
                      <td className="px-3 py-2">
                        <div className="font-medium">{r.matched_name || <span className="text-muted-foreground italic">{t.notMatched}</span>}</div>
                        {r.matched_category && <div className="text-xs text-muted-foreground">{r.matched_category}</div>}
                        {r.supplier_id && <div className="text-[10px] text-emerald-700">✓ {r.supplier_name}</div>}
                      </td>
                      <td className="px-3 py-2 text-center">
                        <Badge variant="outline" className={`${CONF_CLASS(r.confidence)} text-xs`}>
                          {(r.confidence * 100).toFixed(0)}%
                        </Badge>
                        {r.is_duplicate && (
                          <div className="mt-1"><Badge variant="outline" className="bg-amber-100 text-amber-800 border-amber-200 text-[10px]">
                            <Repeat className="w-3 h-3 mr-1" />{t.dup}
                          </Badge></div>
                        )}
                      </td>
                      <td className="px-3 py-2 text-xs text-muted-foreground max-w-[280px] truncate" title={r.text}>{r.text}</td>
                      <td className="px-3 py-2 text-center min-w-[140px]">
                        <Select value={r.selected_kind} onValueChange={(v) => setRowKind(r.temp_id, v)}>
                          <SelectTrigger className="h-8 text-xs" data-testid={`bank-import-kind-select-${r.temp_id}`}><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="revenue">{t.kindRevenue}</SelectItem>
                            <SelectItem value="expense">{t.kindExpense}</SelectItem>
                            <SelectItem value="skip">{t.kindSkip}</SelectItem>
                          </SelectContent>
                        </Select>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardContent>
          </Card>
        </>
      )}

      {/* Import result summary */}
      {result && (
        <Card data-testid="bank-import-result">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-emerald-700">
              <CheckCircle2 className="w-5 h-5" /> {lang === "tr" ? "İçe aktarma tamamlandı" : "Import abgeschlossen"}
            </CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <SummaryStat label={t.resultCreatedRev} value={result.created_revenue} accent="emerald" />
            <SummaryStat label={t.resultCreatedExp} value={result.created_expenses} accent="emerald" />
            <SummaryStat label={t.resultSkippedDup} value={result.skipped_duplicates} accent={result.skipped_duplicates ? "amber" : undefined} />
            <SummaryStat label={t.resultSkippedManual} value={result.skipped_manual} />
            <div className="md:col-span-4">
              <Button onClick={reset} variant="outline" data-testid="bank-import-new-btn">
                <Upload className="w-4 h-4 mr-2" />{lang === "tr" ? "Yeni ekstre" : "Neuer Auszug"}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {!preview && !result && csvText.length === 0 && (
        <div className="text-center text-xs text-muted-foreground py-6">
          <AlertTriangle className="w-4 h-4 inline-block mr-1" />{t.empty}
        </div>
      )}

      {/* Rollback confirmation dialog */}
      <AlertDialog open={rollbackOpen} onOpenChange={setRollbackOpen}>
        <AlertDialogContent data-testid="bank-import-rollback-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{t.rollbackConfirmTitle}</AlertDialogTitle>
            <AlertDialogDescription>{t.rollbackConfirmDesc}</AlertDialogDescription>
          </AlertDialogHeader>
          {lastBatch && (
            <div className="text-xs bg-muted/50 border rounded-lg p-3 font-mono">
              <div><span className="text-muted-foreground uppercase tracking-wide">{t.rollbackBatch}:</span> {lastBatch.batch_id}</div>
              <div className="mt-1"><span className="text-muted-foreground uppercase tracking-wide">{t.rollbackRev}:</span> {lastBatch.revenue_rows} · <span className="text-muted-foreground uppercase tracking-wide">{t.rollbackExp}:</span> {lastBatch.expense_rows}</div>
            </div>
          )}
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="bank-import-rollback-cancel-btn">{t.rollbackCancel}</AlertDialogCancel>
            <AlertDialogAction onClick={doRollback} data-testid="bank-import-rollback-confirm-btn"
                               className="bg-amber-600 hover:bg-amber-700 focus:ring-amber-500">
              <Undo2 className="w-4 h-4 mr-2" />{t.rollbackConfirm}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function SummaryStat({ label, value, accent }) {
  const color = accent === "emerald" ? "text-emerald-700" : accent === "rose" ? "text-rose-600" : accent === "amber" ? "text-amber-700" : "text-foreground";
  return (
    <div className="border rounded-xl p-3 bg-card">
      <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className={`text-xl font-semibold ${color}`}>{value}</div>
    </div>
  );
}
